<template>
    <UnlayerEditorProvider :on-preview-html="() => {}" @editor="(ed) => editor = ed">
        <div v-if="isLoading" class="loading-container">
            <DsSpinner />
        </div>

        <div v-else-if="initError" class="error-container">
            <DsInlineAlert
                type="critical"
                leading-icon
            >
                {{ $t('unlayer.confirmationEmail.errorLoadingEditor') }}
            </DsInlineAlert>
        </div>

        <div v-else class="unlayer-email-edit-stage">
            <UnlayerEditor
                data-qa="unlayer-editor"
                :design="design"
                display-mode="email"
                :options="unlayerOptions"
                :unlayer-user-id="appId"
                :unlayer-project-id="unlayerProjectId"
                :unlayer-builder-version-override="unlayerBuilderVersionOverride"
                :use-unlayer-builder-version-override-enabled="useUnlayerBuilderVersionOverrideEnabled"
                @design-updated="onDesignUpdated"
            />

            <Portal to="confirmation-email-header-actions">
                <div class="actions-container">
                    <DsTextButton
                        leading-icon="eye-show"
                    >
                        {{ $t('global.preview') }}
                    </DsTextButton>

                    <DsFilledButton>
                        {{ $t('global.saveAndExit') }}
                    </DsFilledButton>

                    <DsOutlineButton>
                        {{ $t('unlayer.confirmationEmail.updateDefaultTemplate') }}
                    </DsOutlineButton>

                    <TemplateActions />
                </div>
            </Portal>
        </div>
    </UnlayerEditorProvider>
</template>

<script>
import { toRaw, toRef } from 'vue';
import axios from 'axios';

import UnlayerEditorProvider from '@/components/unlayer/UnlayerEditorProvider';
import UnlayerEditor from '@/components/unlayer/UnlayerEditor';
import TemplateActions from './ConfirmationEmailTemplateActions.vue';

import { createConfirmationEmailOptions } from './unlayerEmail.util';
import brandingEventHandler from '@/components/brandingCenter/eventHandler/index.js';

export default {
    components: {
        UnlayerEditorProvider,
        UnlayerEditor,
        TemplateActions,
    },

    props: {
        appId: String,
        environment: String,
        unlayerBuilderVersionOverride: String,
        useUnlayerBuilderVersionOverrideEnabled: Boolean,
        templateId: String,
        design: Object,
    },

    data() {
        return {
            isLoading: false,
            initError: false,
            unlayerOptions: createConfirmationEmailOptions(),
            unlayerProjectId: null,
            editor: null,
        };
    },

    created() {
        this.init();
        window.addEventListener('message', this.onMessage);
        this.registerReloadDesignCallback(this.reloadDesign);
    },

    beforeDestroy() {
        window.removeEventListener('message', this.onMessage);
    },

    setup(props) {
        const {
            iframeEventHandler,
            designUpdatedEventHandler,
            registerReloadDesignCallback,
        } = brandingEventHandler({
            // TODO: use real template id
            // we can't save templates yet so there is no template id
            templateId: '00000000-0000-0000-0000-000000000001',
            appId: props.appId,
            environment: props.environment,
            design: toRef(props, 'design'),
        });

        return {
            iframeEventHandler,
            designUpdatedEventHandler,
            registerReloadDesignCallback,
        };
    },

    methods: {
        async init() {
            this.isLoading = true;
            this.initError = false;

            try {
                const { data } = await axios.get('/app/unlayerEmailFunnel/builderConfigForUnlayerConfirmationEmail');

                this.unlayerProjectId = data.unlayerProjectId;
            } catch (e) {
                this.initError = true;
            } finally {
                this.isLoading = false;
            }
        },

        onMessage(msg) {
            this.iframeEventHandler(msg);
        },

        onDesignUpdated({ data, change }) {
            this.designUpdatedEventHandler({ data, change });

            this.$emit('design-updated', data);
        },

        reloadDesign(design) {
            toRaw(this.editor)?.loadDesign(design);
        },
    }
};
</script>

<style scoped lang="scss">
.loading-container {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.error-container {
    display: flex;
    justify-content: center;
    margin-top: $spacing-200;
}

.unlayer-email-edit-stage {
    height: 100%;
}

.actions-container {
    display: flex;

    & > * {
        margin: 0 $spacing-050;
    }
}
</style>
